/***
*
*   FEATURE EDITOR
*   Edit features/modules for a plan - fetches available features from API
*
**********/

import { Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

const RESET_INTERVAL_OPTIONS = [
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'concurrent', label: 'Concurrent' }
];

export function FeatureEditor({ features, availableFeatures, onChange }) {
  const enabledFeatures = Object.keys(features || {});

  const handleToggleFeature = (featureKey) => {
    const newFeatures = { ...features };
    const featureInfo = availableFeatures.find(f => f.key === featureKey);

    if (newFeatures[featureKey]) {
      delete newFeatures[featureKey];
    } else {
      newFeatures[featureKey] = {
        feature_key: featureKey,
        name: featureInfo?.name || featureKey,
        helpText: featureInfo?.helpText || '',
        enabled: true,
        limit: null,
        unit: featureInfo?.unit || null,
        resetInterval: featureInfo?.resetInterval ? { type: 'lifetime' } : null
      };
    }

    onChange(newFeatures);
  };

  const handleUpdateLimit = (featureKey, limit) => {
    const newFeatures = { ...features };
    if (newFeatures[featureKey]) {
      newFeatures[featureKey] = {
        ...newFeatures[featureKey],
        limit: limit ? parseInt(limit) : null
      };
    }
    onChange(newFeatures);
  };

  const handleUpdateResetInterval = (featureKey, intervalType) => {
    const newFeatures = { ...features };
    if (newFeatures[featureKey]) {
      newFeatures[featureKey] = {
        ...newFeatures[featureKey],
        resetInterval: intervalType ? { type: intervalType } : null
      };
    }
    onChange(newFeatures);
  };

  // Use availableFeatures from props or fallback to empty array
  const featureList = availableFeatures || [];

  return (
    <div className={Style.featureEditor}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-slate-700">
          Enabled Features ({enabledFeatures.length})
        </h3>
      </div>

      {featureList.length === 0 ? (
        <div className={Style.emptyState}>
          <p className={Style.emptyStateText}>No features available. Add features in the Features tab first.</p>
        </div>
      ) : (
        <div className={Style.modulesList}>
          {featureList.map((feature) => {
            const isEnabled = !!features?.[feature.key];
            const featureData = features?.[feature.key];

            return (
              <div key={feature.key} className={Style.moduleItem}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => handleToggleFeature(feature.key)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${isEnabled
                      ? 'bg-green-500 border-green-500'
                      : 'border-slate-300 hover:border-slate-400'
                      }`}
                  >
                    {isEnabled && <Icon image="check" color="white" size={12} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <span className={Style.moduleName}>{feature.name}</span>
                    <span className={Style.moduleKey}>{feature.key}</span>
                  </div>
                </div>

                {isEnabled && (feature.hasLimit || feature.resetInterval) && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {feature.hasLimit && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          className={Style.featureLimitInput}
                          value={featureData?.limit ?? ''}
                          onChange={(e) => handleUpdateLimit(feature.key, e.target.value)}
                          placeholder="∞"
                          min="0"
                        />
                        {feature.unit && (
                          <span className="text-xs text-slate-500">{feature.unit}</span>
                        )}
                      </div>
                    )}

                    {feature.resetInterval && (
                      <select
                        className={Style.featureIntervalSelect}
                        value={featureData?.resetInterval?.type || 'lifetime'}
                        onChange={(e) => handleUpdateResetInterval(feature.key, e.target.value)}
                      >
                        {RESET_INTERVAL_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}